import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import React from 'react'

type Props = {
    title: string
    children: React.ReactNode
    onClose: () => void
    leftFooter?: React.ReactNode
    rightFooter?: React.ReactNode
    size?: 'small' | 'medium' | 'large' | 'xl' | 'custom' // Add predefined size options
    customWidth?: string // Optional custom width if 'custom' size is selected
    classes?: string // Optional additional classes
    allowUserToClose?: boolean
}

function Modal({
    title,
    children,
    onClose,
    leftFooter,
    rightFooter,
    size = 'medium',
    customWidth,
    classes,
    allowUserToClose = true,
}: Props): React.JSX.Element {
    const modalClasses = clsx(
        classes,
        'bg-white rounded-2xl shadow-lg w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden',
        {
            'max-w-sm': size === 'small',
            'max-w-5xl': size === 'large',
            'max-w-7xl': size === 'xl',
            [customWidth || '']: size === 'custom', // Custom width fallback
        },
    )

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={allowUserToClose ? onClose : undefined}
        >
            <div
                className={modalClasses}
                onClick={(e) => e.stopPropagation()} // Prevent closing modal when clicking inside it
            >
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-lg font-semibold">{title}</h2>
                    {allowUserToClose && (
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:bg-gray-100 p-2 rounded-full w-8 h-8 flex items-center justify-center"
                        >
                            <FontAwesomeIcon
                                icon={faXmark}
                                className="text-xl"
                            />
                        </button>
                    )}
                </div>

                {/* Main Content */}
                <div className="p-4 overflow-y-auto flex-1">{children}</div>

                {/* Footer */}
                {(leftFooter || rightFooter) && (
                    <div className="p-4 border-t flex justify-between">
                        <div>{leftFooter}</div>
                        <div className="flex space-x-2">{rightFooter}</div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default Modal
